import React, { createContext, Context, useState, PropsWithChildren, FC, useEffect, useRef }  from 'react';
import API from './assets/scoreboard_end_game.json';

export const ScoreboardContext : Context<object> = createContext({});

export enum CHECK_CODE {
    OK = 101,
    DOWN = 104,
    CHECKER_DISABLED = 109,
    ERROR = 110,
}

export enum CTF_STATUS {
    NOT_STARTED,
    STARTED,
    FINISHED
}

export type ScoreboardContextType = {
    data: DataType,
    refresh: () => void
}

export type DataType = {
    game: GameData|null,
    teams : TeamData[]|null,
    services : ServiceData[]|null,
    koth : ExtData[]|null,
    speedrun : ExtData[]|null,
    ctf_start: string,
    ctf_status: CTF_STATUS,
    ctf_round: number,
    total_rounds: number
}

export type ExtData = {
    teamId: number,
    score: number
}

export type TeamData = {
    name: string,
    logo: string,
    full_score: number,
    teamId : number,
    nop: boolean
}

export type ServiceData = {
    name: string,
    startingScore: number,
    timeout: number
}

export type GameData = {
    checks: {
        action: string,
        exitCode: number,
        serviceId: number,
        stdout: string,
        teamId: number
    }[],
    first_bloods: {
        attacker_id: number,
        service_id: number,
        timestamp: string
    }[],
    score: {
        lost: number,
        score: number,
        serviceId: number,
        stolen: number,
        teamId: number
    }[],
    sla: {
        serviceId: number,
        successfulChecks: number,
        teamId: number,
        totalChecks: number
    }[]
}

const getTeamFullScore = (id : number, data : DataType) => {
    if(data.ctf_status === CTF_STATUS.NOT_STARTED) return 0;
    if(data.game === null || data.speedrun === null || data.koth === null || data.teams === null) return 0;
    

    const max_speedrun_score = Math.max(...data.speedrun?.map(t => t.score), 1)
    const max_koth_score = Math.max(...data.koth?.map(t => t.score), 1)
    const max_ad_score = Math.max(
    ...data.teams.map(
        (t, teamid) => 
            data.services != null ? data.services.filter((s, sid) => new Set(data.game?.checks.filter(flt => flt.exitCode != CHECK_CODE.CHECKER_DISABLED).map(flt => flt.serviceId)).has(sid)).map(
                (s, serviceid) => (data.game != null ? data.game?.score.filter(tmpscore => tmpscore.teamId == teamid && tmpscore.serviceId == serviceid)[0].score * (data.game?.sla.filter(t => t.teamId === teamid && t.serviceId === serviceid)[0].totalChecks === 0 ? 1 : data.game?.sla.filter(t => t.teamId === teamid && t.serviceId === serviceid)[0].successfulChecks / data.game?.sla.filter(t => t.teamId === teamid && t.serviceId === serviceid)[0].totalChecks) : 0)).reduce((sum, accumulator) => sum + accumulator, 0) : 0))

    const koth_score = id !== 0 ? data.koth.filter(t => t.teamId === id)[0].score : 0
    const speedrun_score = data.speedrun.filter(t => t.teamId === id)[0].score;
    const ad_score = data.game?.score.filter(f => data.game?.checks.filter(ff => ff.exitCode != CHECK_CODE.CHECKER_DISABLED).map(ff => ff.serviceId).includes(f.serviceId)).filter(f => f.teamId === id).map((f, sid) => {
        if(data.game === null) return 0;
        const sla_entry = data.game?.sla.filter(t => t.teamId === id && t.serviceId === f.serviceId)[0]
        const sla : number = (sla_entry.totalChecks === 0 ? 1 : sla_entry.successfulChecks / sla_entry.totalChecks)
        const ad_score : number = (f.score * sla);  
        return ad_score;
    }).reduce((sum, current) => sum + current, 0);
    /*console.log({
            teamName: data.teams?.at(id)?.name,
            id: id,
            ad_score: Math.round(ad_score),
            speedrun_score: speedrun_score,
            koth_score: koth_score,
            max_ad_score: max_ad_score,
            max_koth_score: max_koth_score,
            max_speedrun_score: max_speedrun_score
        })*/
    return Math.round(ad_score * .7 + .15 * speedrun_score * max_ad_score / max_speedrun_score + .15 * koth_score * max_ad_score / max_koth_score)
}

const ScoreboardContextProvider: FC<PropsWithChildren> = ({children}) => {
    const debug = false
    const useRefresh = false
    const API_URL = "/scoreboard"
    const [structure, setStructure] = useState<object | null>(null);
    const isInitialised = useRef(0);
    useEffect(() => {
        if(isInitialised.current) return;
        if(useRefresh){
            setTimeout(() => {
                setInterval(() => {
                    refresh()
                }, !debug ? 60 * 1000 : 5 * 1000) // every 60 seconds
                refresh()
            }, (60 - new Date().getSeconds()) * 1000)
        } 
        isInitialised.current = 1;
        refresh()
    }, [])
    const refresh = async () => {
        let struct : DataType = {
            game: null,
            teams: null,
            services: null,
            koth: null,
            speedrun: null,
            ctf_start: "",
            ctf_status: CTF_STATUS.NOT_STARTED,
            ctf_round: -1,
            total_rounds: 0
        };
        //const response : any = await (await fetch(API_URL)).json()
        const response : any = API
        struct.game = response.AD_data
        struct.teams = response.team_data.teams
        struct.services = response.team_data.services
        struct.koth = response.koth_data
        struct.speedrun = response.bot_data
        struct.ctf_start = response.team_data.start
        struct.ctf_round = response.AD_round
        struct.total_rounds = response.team_data.rounds

        if(response.AD_round < 0) struct.ctf_status = CTF_STATUS.NOT_STARTED;
        else if(response.AD_round >= response.team_data.rounds) struct.ctf_status = CTF_STATUS.FINISHED;
        else struct.ctf_status = CTF_STATUS.STARTED;

        if(struct.teams != null) struct.teams.forEach((t, i) => {
            t.full_score = getTeamFullScore(i, struct)
            t.teamId = i
        })

        if(struct.teams != null && struct.services != null && struct.game != null)
        for(let i = 0; i < struct.teams?.length; i++)
            for(let j = 0; j < struct.services?.length; j++)
            if(struct.game.score.filter(f => f.serviceId === j && f.teamId === i).length <= 0)
                struct.game.score.push({
                    score: 0,
                    lost: 0,
                    stolen: 0,
                    teamId: i,
                    serviceId: j
                })

        setStructure(struct)
        if(debug) console.log(struct)
        const d = new Date()
        console.log(d.getHours() + ":" +d.getMinutes() + ":" +d.getSeconds() + " - Scoreboard refreshed.")
    }
    return (
        <ScoreboardContext.Provider value={{data: structure, refresh: refresh}}>{children}</ScoreboardContext.Provider>
    )
}

export default ScoreboardContextProvider;